import React from 'react';
import { Grade } from '../../services/api/domains/grades/types';
import { modalStyles as styles } from './styles';
import useEditableModal from './hooks/useEditableModal';
import ModalFieldRenderer from './ModalFieldRenderer';
import {
  gradeFields,
  gradeIdField,
  gradeAlwaysDisabledFields,
} from './configs/gradeFields';

interface GradeModalProps {
  grade: Grade;
  initialMode?: 'EDIT' | 'VIEW';
  save: (editedGrade: Grade) => void;
  close: () => void;
}

const GradeModal: React.FC<GradeModalProps> = ({
  grade,
  initialMode = 'VIEW',
  save,
  close,
}) => {
  // Use the hook for all modal logic
  const {
    editedEntity: editedGrade,
    setEditedEntity: setEditedGrade,
    modalMode,
    focusedField,
    setFocusedField,
    handleSave,
    handleClose,
    isFieldDisabled,
    getFloatingLabelStyle,
    getFloatingInputStyle,
    getSaveButtonStyle,
    getSaveButtonHoverStyle,
    getSaveButtonFocusStyle,
    getCloseButtonStyle,
    getCloseButtonHoverStyle,
    getCloseButtonFocusStyle,
    getSaveButtonText,
    getCloseButtonText,
  } = useEditableModal({
    entity: grade,
    initialMode,
    onSave: save,
    onClose: close,
    alwaysDisabledFields: gradeAlwaysDisabledFields,
    styles,
  });

  return (
    <div style={styles.modalOverlayStyle}>
      <div style={styles.modalContentStyle}>
        <div style={styles.modalHeaderStyle}>
          <h2 style={styles.modalTitleStyle}>
            {modalMode === 'VIEW'
              ? 'Product Type Details'
              : modalMode === 'EDIT'
                ? 'Edit Product Type'
                : 'Add Product Type'}
          </h2>
          <button
            onClick={handleClose}
            style={styles.modalCloseXStyle}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, styles.modalCloseXHoverStyle)
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, styles.modalCloseXStyle)
            }>
            ×
          </button>
        </div>

        <div style={styles.modalBodyStyle}>
          {/* All grade fields including ID and audit fields */}
          <ModalFieldRenderer
            fields={[gradeIdField, ...gradeFields]}
            entity={editedGrade}
            setEntity={setEditedGrade}
            mode={modalMode}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            isFieldDisabled={isFieldDisabled}
            getFloatingLabelStyle={getFloatingLabelStyle}
            getFloatingInputStyle={getFloatingInputStyle}
            styles={styles}
            missingMessage="Unknown"
          />
        </div>

        <div style={styles.modalFooterStyle}>
          <button
            onClick={handleClose}
            style={getCloseButtonStyle()}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, getCloseButtonHoverStyle())
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, getCloseButtonStyle())
            }
            onFocus={e =>
              Object.assign(e.currentTarget.style, getCloseButtonFocusStyle())
            }
            onBlur={e =>
              Object.assign(e.currentTarget.style, getCloseButtonStyle())
            }>
            {getCloseButtonText()}
          </button>
          <button
            onClick={handleSave}
            style={getSaveButtonStyle()}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, getSaveButtonHoverStyle())
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, getSaveButtonStyle())
            }
            onFocus={e =>
              Object.assign(e.currentTarget.style, getSaveButtonFocusStyle())
            }
            onBlur={e =>
              Object.assign(e.currentTarget.style, getSaveButtonStyle())
            }>
            {getSaveButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(GradeModal);
